import {useCallback} from 'react';

import {useReadOnlyDataHook} from 'src/hooks/use-read-only-data-hook';
import {getVendorById} from 'src/openapi/admin/apis/VendorManagementApi';
import {useAppDispatch} from 'src/store';

import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {Vendor} from 'src/openapi/admin/models/Vendor';

type Options = {
    enabled?: boolean,
};

type Return = {
    vendor: Vendor | null,
    loading: boolean,
    error: ApiError | null,
    reload: () => Promise<void>,
};

export const useVendor = (vendorId?: string, options?: Options): Return => {
    const dispatch = useAppDispatch();
    const shouldLoadData = Boolean(vendorId) && (options?.enabled ?? true);

    const getDispatchAction = useCallback((signal: AbortSignal) => {
        if (!vendorId) {
            return Promise.reject(new Error('Vendor id is required'));
        }
        return dispatch(getVendorById({id: vendorId}, {signal}));
    }, [dispatch, vendorId]);

    const {
        data,
        isLoading,
        loadData,
        loadingError,
    } = useReadOnlyDataHook<Vendor | null>({
        initValue: null,
        getDispatchAction,
        shouldLoadData,
    });

    return {
        vendor: data,
        loading: isLoading,
        error: loadingError,
        reload: loadData,
    };
};
